// Real Possinote API integration using HTTP requests
// Uses environment variables for configuration

const POSSINOTE_API_KEY = process.env.NEXT_PUBLIC_POSSINOTE_API_KEY || 'pk_6xsggije.cf4459ba42e41075cdf9ff026ec5dc50f65fa13e736b83691efdd79ff7c33a50';
const POSSINOTE_BASE_URL = 'https://notifyapi.possitech.net/api/v1';
const SALES_EMAIL = process.env.NEXT_PUBLIC_SALES_EMAIL || 'possibletechnology@gmail.com';
const SALES_PHONE = process.env.NEXT_PUBLIC_SALES_PHONE_NUMBER || '+233244469369';

// Data interface for demo request
const DemoRequestData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  jobTitle: '',
  eventType: '',
  expectedAttendees: '',
  currentSolution: '',
  timeline: '',
  budget: '',
  specificNeeds: '',
  preferredDate: '',
  preferredTime: '',
  additionalNotes: ''
};

// Lead scoring algorithm
const calculateLeadScore = (data) => {
  let leadScore = 0;
  let leadScoreColor = '#ef4444';
  let leadScoreText = 'Low Priority';

  // Score based on event size (larger events = higher priority)
  if (data.event_size && (data.event_size.includes('1000') || data.event_size.includes('5000'))) leadScore += 3;
  else if (data.event_size && data.event_size.includes('500')) leadScore += 2;
  else if (data.event_size && data.event_size.includes('100')) leadScore += 1;

  // Score based on phone number (direct contact = higher priority)
  if (data.phone) leadScore += 2;

  // Score based on additional information (detailed requirements = higher priority)
  if (data.message && data.message.length > 20) leadScore += 1;

  // Score based on event type (corporate events = higher priority)
  if (data.event_type && (data.event_type === 'corporate' || data.event_type === 'conference')) leadScore += 1;

  // Score based on event date (nearer dates = higher priority)
  if (data.event_date) {
    const eventDate = new Date(data.event_date);
    const today = new Date();
    const daysUntilEvent = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntilEvent <= 30) leadScore += 2;
    else if (daysUntilEvent <= 90) leadScore += 1;
  }

  if (leadScore >= 6) {
    leadScoreColor = '#10b981';
    leadScoreText = 'High Priority';
  } else if (leadScore >= 3) {
    leadScoreColor = '#fbbf24';
    leadScoreText = 'Medium Priority';
  }

  return { leadScore, leadScoreColor, leadScoreText };
};

// Smart SMS truncation helper
const truncateSMS = (message, maxLength = 159) => {
  if (message.length <= maxLength) return message;
  
  // Try to truncate at word boundaries first
  const truncated = message.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.8) { // If we can find a space in the last 20% of the message
    return truncated.substring(0, lastSpace);
  }
  
  return truncated;
};

// SMS Templates (Character Limited)
const createRequesterSMSTemplate = (data) => {
  const fullName = data.name || 'Valued Customer';
  const message = `Hi ${fullName}! Your Possitech Events demo request received. Sales team will contact you within 24h. Thank you! - Possitech`;
  return truncateSMS(message, 159);
};

const createSalesTeamSMSTemplate = (data) => {
  const fullName = data.name || 'Unknown Customer';
  const eventType = (data.event_type || 'Event').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  const company = data.company || 'Unknown Company';
  const email = data.email || 'No email provided';
  const eventSize = data.event_size || 'Unknown size';
  const message = `NEW DEMO: ${fullName} - ${company} - ${eventType} (${eventSize}). Email: ${email}. Contact within 24h!`;
  return truncateSMS(message, 159);
};

// Email Templates
const createRequesterEmailTemplate = (data) => {
  const fullName = data.name || 'Valued Customer';
  const eventType = (data.event_type || 'Event').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8f9fa;">
      <div style="background: linear-gradient(135deg, #fbbf24 0%, #f472b6 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
        <h1 style="color: white; margin: 0; font-size: 28px;">🎯 Possitech Events Demo Request Confirmed!</h1>
      </div>
      
      <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <h2 style="color: #333; margin-bottom: 20px;">Hello ${fullName},</h2>
        
        <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
          Thank you for your interest in Possitech Events! We've received your demo request for <strong>${eventType}</strong> and our sales team has been notified.
        </p>
        
        <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0;">
          <h3 style="color: white; margin: 0 0 10px 0;">🚀 What Happens Next</h3>
          <p style="color: white; margin: 0; opacity: 0.9;">
            Our sales team will contact you within <strong>24 hours</strong> to schedule your personalized demo!
          </p>
        </div>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0; font-size: 16px;">📋 Your Request Summary</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; font-size: 14px;">
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Event Type:</strong> ${eventType}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Company:</strong> ${data.company || 'Not provided'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Event Date:</strong> ${data.event_date || 'Not specified'}</p>
            </div>
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Expected Attendees:</strong> ${data.event_size || 'Not specified'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Phone:</strong> ${data.phone || 'Not provided'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Additional Info:</strong> ${data.message ? (data.message.length > 50 ? data.message.substring(0, 50) + '...' : data.message) : 'None provided'}</p>
            </div>
          </div>
        </div>
        
        <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
          We're excited to show you how Possitech Events can transform your ${eventType.toLowerCase()} events with real-time registration, check-ins, and seamless user experience.
        </p>
        
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
          <p style="color: #999; font-size: 14px; margin: 0;">
            Best regards,<br>
            <strong>The Possitech Events Team</strong><br>
            <a href="mailto:support@possitech.net" style="color: #fbbf24;">support@possitech.net</a>
          </p>
        </div>
      </div>
    </div>
  `;
};

const createSalesTeamEmailTemplate = (data) => {
  const fullName = data.name || 'Unknown Customer';
  const eventType = (data.event_type || 'Event').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  const { leadScore, leadScoreColor, leadScoreText } = calculateLeadScore(data);
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; background-color: #f8f9fa;">
      <div style="background: linear-gradient(135deg, #ef4444 0%, #f472b6 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
        <h1 style="color: white; margin: 0; font-size: 28px;">🔥 NEW POSSITECH EVENTS DEMO REQUEST - ${leadScoreText.toUpperCase()}</h1>
        <p style="color: white; margin: 10px 0 0 0; opacity: 0.9;">Lead Score: ${leadScore}/8 | Submitted: ${new Date().toLocaleString()}</p>
      </div>
      
      <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        
        <!-- Lead Priority Indicator -->
        <div style="background-color: ${leadScoreColor}15; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid ${leadScoreColor};">
          <h3 style="color: ${leadScoreColor}; margin: 0; font-size: 18px;">🎯 Lead Priority: ${leadScoreText}</h3>
          <p style="color: #666; margin: 5px 0 0 0; font-size: 14px;">Score: ${leadScore}/8 | Contact within ${leadScore >= 6 ? '4 hours' : leadScore >= 3 ? '12 hours' : '24 hours'}</p>
        </div>
        
        <!-- Contact Information -->
        <div style="background-color: #fef2f2; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ef4444;">
          <h3 style="color: #333; margin-top: 0; font-size: 18px;">👤 CONTACT INFORMATION</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Full Name:</strong> ${fullName}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Email:</strong> <a href="mailto:${data.email}" style="color: #ef4444; text-decoration: none;">${data.email}</a></p>
              <p style="color: #666; margin: 5px 0;"><strong>Phone:</strong> ${data.phone ? `<a href="tel:${data.phone}" style="color: #ef4444; text-decoration: none;">${data.phone}</a>` : 'Not provided'}</p>
            </div>
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Company:</strong> ${data.company}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Job Title:</strong> ${data.jobTitle || 'Not provided'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Submission Time:</strong> ${new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <!-- Event Details -->
        <div style="background-color: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #60a5fa;">
          <h3 style="color: #333; margin-top: 0; font-size: 18px;">🎪 EVENT DETAILS</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Event Type:</strong> <span style="background-color: #60a5fa20; padding: 2px 8px; border-radius: 4px; font-weight: bold;">${eventType}</span></p>
              <p style="color: #666; margin: 5px 0;"><strong>Expected Attendees:</strong> ${data.event_size || 'Not specified'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Event Date:</strong> ${data.event_date || 'Not specified'}</p>
            </div>
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Phone Number:</strong> ${data.phone || 'Not provided'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Additional Info:</strong> <span style="background-color: #fbbf2420; padding: 2px 8px; border-radius: 4px; font-weight: bold;">${data.message ? (data.message.length > 30 ? data.message.substring(0, 30) + '...' : data.message) : 'None provided'}</span></p>
              <p style="color: #666; margin: 5px 0;"><strong>Lead Quality:</strong> ${data.phone ? 'High (Phone provided)' : 'Medium (Email only)'}</p>
            </div>
          </div>
        </div>
        
        <!-- Scheduling Preferences -->
        <div style="background-color: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #10b981;">
          <h3 style="color: #333; margin-top: 0; font-size: 18px;">📅 SCHEDULING PREFERENCES</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Event Date:</strong> ${data.event_date || 'Not specified'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Event Size:</strong> ${data.event_size || 'Not specified'}</p>
            </div>
            <div>
              <p style="color: #666; margin: 5px 0;"><strong>Urgency Level:</strong> ${data.event_date ? (() => {
                const eventDate = new Date(data.event_date);
                const today = new Date();
                const daysUntilEvent = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
                return daysUntilEvent <= 30 ? '🔥 HIGH' : daysUntilEvent <= 90 ? '⚡ MEDIUM' : '📅 LOW';
              })() : '📅 FLEXIBLE'}</p>
              <p style="color: #666; margin: 5px 0;"><strong>Event Scale:</strong> ${data.event_size && data.event_size.includes('1000') ? '🏢 ENTERPRISE' : data.event_size && data.event_size.includes('500') ? '🏢 MID-MARKET' : '🏠 SMALL BUSINESS'}</p>
            </div>
          </div>
        </div>
        
        ${data.additionalNotes ? `
        <!-- Additional Notes -->
        <div style="background-color: #fefce8; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #fbbf24;">
          <h3 style="color: #333; margin-top: 0; font-size: 18px;">📝 ADDITIONAL NOTES & REQUIREMENTS</h3>
          <p style="color: #666; margin: 5px 0; font-style: italic; line-height: 1.6; background-color: #fefce8; padding: 15px; border-radius: 6px; border-left: 3px solid #fbbf24;">${data.additionalNotes}</p>
        </div>
        ` : ''}
        
        <!-- Action Required -->
        <div style="background: linear-gradient(135deg, #fbbf24 0%, #f472b6 100%); padding: 25px; border-radius: 8px; text-align: center; margin: 20px 0;">
          <h3 style="color: white; margin: 0 0 15px 0; font-size: 20px;">⚡ IMMEDIATE ACTION REQUIRED</h3>
          <p style="color: white; margin: 0 0 10px 0; font-size: 16px; opacity: 0.9;">
            <strong>Contact ${fullName} within ${leadScore >= 6 ? '4 hours' : leadScore >= 3 ? '12 hours' : '24 hours'}</strong>
          </p>
          <div style="display: flex; justify-content: center; gap: 20px; margin-top: 15px;">
            <a href="mailto:${data.email}" style="background-color: white; color: #f472b6; padding: 10px 20px; border-radius: 6px; text-decoration: none; font-weight: bold;">📧 Send Email</a>
            ${data.phone ? `<a href="tel:${data.phone}" style="background-color: white; color: #f472b6; padding: 10px 20px; border-radius: 6px; text-decoration: none; font-weight: bold;">📞 Call Now</a>` : ''}
          </div>
        </div>
        
        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
                  <p style="color: #999; font-size: 14px; margin: 0;">
          <strong>Possitech Events Demo System</strong> | Automated Lead Notification<br>
          Generated on ${new Date().toLocaleString()} | Lead ID: ${Date.now()}
        </p>
        </div>
      </div>
    </div>
  `;
};

// Real HTTP-based notification function using Possinote API
export const sendDemoNotifications = async (data) => {
  const results = {
    requesterEmail: false,
    salesTeamEmail: false,
    requesterSMS: false,
    salesTeamSMS: false,
    errors: []
  };

  // Check if API key is configured
  if (!POSSINOTE_API_KEY || POSSINOTE_API_KEY === 'YOUR_API_KEY') {
    results.errors.push('Please configure your Possinote API key in demoNotifications.js');
    return results;
  }

  try {
    console.log('🔑 Using API Key:', POSSINOTE_API_KEY.substring(0, 20) + '...');
    console.log('📧 Sales Email:', SALES_EMAIL);
    console.log('📱 Sales Phone:', SALES_PHONE);
    
    // Send email to requester
    try {
      console.log('📧 Sending requester email to:', data.email);
      const requesterEmailPayload = {
        recipient: data.email,
        subject: '🎯 Your Possitech Events Demo Request Confirmed!',
        content: createRequesterEmailTemplate(data),
        sender_name: 'registration@possitech.net'
      };
      console.log('📧 Requester email payload:', requesterEmailPayload);
      
      const requesterEmailResponse = await fetch(`${POSSINOTE_BASE_URL}/emails/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${POSSINOTE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requesterEmailPayload)
      });

      console.log('📧 Requester email response status:', requesterEmailResponse.status);
      const requesterEmailData = await requesterEmailResponse.json();
      console.log('📧 Requester email response data:', requesterEmailData);
      
      if (requesterEmailData.success) {
        results.requesterEmail = true;
        console.log('✅ Requester email sent successfully:', requesterEmailData.message_id);
      } else {
        console.log('❌ Requester email failed:', requesterEmailData);
        results.errors.push(`Failed to send requester email: ${requesterEmailData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.log('💥 Requester email error:', error);
      results.errors.push(`Requester email error: ${error.message}`);
    }

    // Send email to sales team
    try {
      console.log('📧 Sending sales team email to:', SALES_EMAIL);
      const salesEmailPayload = {
        recipient: SALES_EMAIL,
        subject: '🔥 NEW DEMO REQUEST - Action Required',
        content: createSalesTeamEmailTemplate(data),
        sender_name: 'registration@possitech.net'
      };
      console.log('📧 Sales team email payload:', salesEmailPayload);
      
      const salesEmailResponse = await fetch(`${POSSINOTE_BASE_URL}/emails/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${POSSINOTE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(salesEmailPayload)
      });

      console.log('📧 Sales team email response status:', salesEmailResponse.status);
      const salesEmailData = await salesEmailResponse.json();
      console.log('📧 Sales team email response data:', salesEmailData);
      
      if (salesEmailData.success) {
        results.salesTeamEmail = true;
        console.log('✅ Sales team email sent successfully:', salesEmailData.message_id);
      } else {
        console.log('❌ Sales team email failed:', salesEmailData);
        results.errors.push(`Failed to send sales team email: ${salesEmailData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.log('💥 Sales team email error:', error);
      results.errors.push(`Sales team email error: ${error.message}`);
    }

    // Send SMS to requester (if phone provided)
    if (data.phone) {
      try {
        console.log('📱 Sending requester SMS to:', data.phone);
        const requesterSMSMessage = createRequesterSMSTemplate(data);
        const requesterSMSPayload = {
          sms: {
            to: data.phone,
            message: requesterSMSMessage,
            sender_id: 'Possitech'
          }
        };
        console.log('📱 Requester SMS message length:', requesterSMSMessage.length);
        console.log('📱 Requester SMS payload:', requesterSMSPayload);
        
        const requesterSMSResponse = await fetch(`${POSSINOTE_BASE_URL}/sms/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${POSSINOTE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requesterSMSPayload)
        });

        console.log('📱 Requester SMS response status:', requesterSMSResponse.status);
        const requesterSMSData = await requesterSMSResponse.json();
        console.log('📱 Requester SMS response data:', requesterSMSData);
        
        if (requesterSMSData.success) {
          results.requesterSMS = true;
          console.log('✅ Requester SMS sent successfully:', requesterSMSData.data.message_id);
        } else {
          console.log('❌ Requester SMS failed:', requesterSMSData);
          results.errors.push(`Failed to send requester SMS: ${requesterSMSData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.log('💥 Requester SMS error:', error);
        results.errors.push(`Requester SMS error: ${error.message}`);
      }
    } else {
      console.log('📱 No phone number provided for requester SMS');
    }

          // Send SMS to sales team
      try {
        console.log('📱 Sending sales team SMS to:', SALES_PHONE);
        const salesSMSMessage = createSalesTeamSMSTemplate(data);
        const salesSMSPayload = {
          sms: {
            to: SALES_PHONE,
            message: salesSMSMessage,
            sender_id: 'Possitech'
          }
        };
        console.log('📱 Sales team SMS message length:', salesSMSMessage.length);
        console.log('📱 Sales team SMS payload:', salesSMSPayload);
      
      const salesSMSResponse = await fetch(`${POSSINOTE_BASE_URL}/sms/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${POSSINOTE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(salesSMSPayload)
      });

      console.log('📱 Sales team SMS response status:', salesSMSResponse.status);
      const salesSMSData = await salesSMSResponse.json();
      console.log('📱 Sales team SMS response data:', salesSMSData);
      
      if (salesSMSData.success) {
        results.salesTeamSMS = true;
        console.log('✅ Sales team SMS sent successfully:', salesSMSData.data.message_id);
      } else {
        console.log('❌ Sales team SMS failed:', salesSMSData);
        results.errors.push(`Failed to send sales team SMS: ${salesSMSData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.log('💥 Sales team SMS error:', error);
      results.errors.push(`Sales team SMS error: ${error.message}`);
    }

  } catch (error) {
    results.errors.push(`General notification error: ${error.message}`);
  }

  return results;
};

export { calculateLeadScore, createRequesterEmailTemplate, createSalesTeamEmailTemplate };
